<template>
  <el-container class="layout-container" :class="{ 'dashboard-fullscreen': appStore.isDashboardFullscreen }">
    <el-aside 
      :width="sidebarDisplayWidth" 
      class="left-aside"
      :class="{ resizing: isResizing, collapsed: settingsStore.sidebarCollapsed }"
      v-show="!appStore.isDashboardFullscreen"
    >
      <div class="logo" v-show="!settingsStore.sidebarCollapsed">
        <span>{{ t('common.appName') }}</span>
      </div>
      <SideMenu v-show="!settingsStore.sidebarCollapsed" :is-collapse="settingsStore.sidebarCollapsed" />
      <div class="collapse-trigger" @click="settingsStore.toggleSidebarCollapse">
        <el-icon><component :is="settingsStore.sidebarCollapsed ? Expand : Fold" /></el-icon>
      </div>
      
      <!-- Resize Handle -->
      <div 
        v-if="!settingsStore.sidebarCollapsed"
        class="resize-handle"
        :class="{ resizing: isResizing }"
        @mousedown="startResize"
      ></div>
    </el-aside>
    
    <el-container>
      <el-header class="main-header" v-show="!appStore.isDashboardFullscreen">
        <div class="header-left">
          <el-breadcrumb separator="/">
            <el-breadcrumb-item 
              v-for="(item, index) in breadcrumbs" 
              :key="index"
              :to="item.path"
            >
              {{ item.title }}
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="header-right">
          <!-- 全局租户切换器（TenantSwitcher）：多租户显示下拉；仅全局角色时显示"全局"单选项（不可切换其他租户） -->
          <el-dropdown
            v-if="authStore.isAuthenticated && tenantOptions.length > 0"
            @command="handleTenantSwitch"
            popper-class="floating-dropdown-popper"
          >
            <span class="tenant-switcher">
              <el-icon><OfficeBuilding /></el-icon>
              {{ currentTenantLabel }}
              <el-icon class="el-icon--right"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item
                  v-for="tenant in tenantOptions"
                  :key="tenant.tenant_id ?? 'global'"
                  :command="tenant.tenant_id"
                  :disabled="tenant.is_global"
                  :class="{ 'is-active': tenant.tenant_id === authStore.currentTenantId }"
                >
                  {{ tenant.tenant_name }}
                </el-dropdown-item>
                <el-dropdown-item v-if="globalViewOnly" disabled divided>
                  {{ t('routes.tenant-select.globalViewHint') }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <el-tag v-else-if="authStore.isAuthenticated && authStore.currentTenant" class="tenant-static" size="small">
            {{ authStore.currentTenant.tenant_name }}
          </el-tag>

          <el-dropdown @command="handleLocaleChange" popper-class="floating-dropdown-popper">
            <span class="el-dropdown-link">
              <el-icon><ChatRound /></el-icon>
              {{ currentLocaleName }}
              <el-icon class="el-icon--right"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item 
                  v-for="locale in availableLocales" 
                  :key="locale.code"
                  :command="locale.code"
                  :class="{ 'is-active': locale.code === currentLocaleCode }"
                >
                  {{ locale.nativeName }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          
          <el-button @click="appStore.toggleTheme" circle>
            <el-icon><component :is="appStore.isDark ? Sunny : Moon" /></el-icon>
          </el-button>
          
          <el-dropdown v-if="authStore.isAuthenticated" popper-class="floating-dropdown-popper">
            <span class="user-info">
              <el-avatar :size="28" :icon="UserFilled" />
              {{ authStore.user?.display_name ?? authStore.user?.username ?? 'User' }}
              <el-icon><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="goToProfile">
                  <el-icon><User /></el-icon>
                  {{ t('common.profile') }}
                </el-dropdown-item>
                <el-divider style="margin: 6px 0" />
                <el-dropdown-item @click="logout">
                  <el-icon><SwitchButton /></el-icon>
                  {{ t('common.logout') }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <StandardButton
            v-else
            action="submit"
            :custom-text="t('common.login')"
            @click="$router.push('/login')"
          />
        </div>
      </el-header>
      
      <el-container class="content-container" :class="{ 'dashboard-route': isDashboardRoute }">
        <el-main class="main-content" :class="{ 'dashboard-route': isDashboardRoute }">
          <router-view :key="$route.fullPath" />
        </el-main>
        
        <el-aside
          :width="helpDisplayWidth"
          class="right-help"
          :class="{ collapsed: settingsStore.helpCollapsed }"
          v-show="!appStore.isDashboardFullscreen"
        >
          <HelpPanel v-show="!settingsStore.helpCollapsed" />
          <div
            class="help-collapse-trigger"
            :title="t(settingsStore.helpCollapsed ? 'common.expandHelp' : 'common.collapseHelp')"
            :aria-label="t(settingsStore.helpCollapsed ? 'common.expandHelp' : 'common.collapseHelp')"
            @click="settingsStore.toggleHelpCollapse"
          >
            <el-icon><component :is="settingsStore.helpCollapsed ? Expand : Fold" /></el-icon>
          </div>
        </el-aside>
      </el-container>
    </el-container>
  </el-container>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import SideMenu from './SideMenu.vue'
import HelpPanel from './HelpPanel.vue'
import { useAppStore } from '../stores/app'
import { useAuthStore } from '../stores/auth'
import { useUserSettingsStore } from '../stores/userSettings'
import { Expand, Fold, Moon, Sunny, ArrowDown, UserFilled, User, ChatRound, SwitchButton, OfficeBuilding } from '@element-plus/icons-vue'
import { getAvailableLocales, getLocale, type LocaleConfig } from '@/i18n'
import StandardButton from '@/components/StandardButton.vue'
import type { TenantInfo } from '@/utils/authStorage'
import { ElMessage } from 'element-plus'

const { t } = useI18n()
const appStore = useAppStore()
const authStore = useAuthStore()
const settingsStore = useUserSettingsStore()
const router = useRouter()
const route = useRoute()

const isResizing = ref(false)
const startX = ref(0)
const startWidth = ref(0)
const currentWidth = ref(0)
let rafId: number | null = null

const sidebarDisplayWidth = computed(() => {
  if (settingsStore.sidebarCollapsed) return '36px'
  // 拖拽时使用本地状态，避免频繁更新 store
  return isResizing.value ? `${currentWidth.value}px` : `${settingsStore.sidebarWidth}px`
})

const availableLocales = computed<LocaleConfig[]>(() => getAvailableLocales())

const currentLocaleCode = computed(() => getLocale())

const currentLocaleName = computed(() => {
  const current = availableLocales.value.find(l => l.code === currentLocaleCode.value)
  return current?.nativeName || currentLocaleCode.value
})

/** 是否仅持有全局角色（无任何租户岗位）——Header 显示"全局"切换入口（不可切换其他租户）。 */
const globalViewOnly = computed(() =>
  authStore.availableTenants.length > 0 &&
  authStore.availableTenants.every((item) => item.is_global)
)

/** 可切换租户列表（非全局角色条目）；仅全局角色时构造"全局"单选项（tenant_id=null，禁用切换）。 */
const tenantOptions = computed<TenantInfo[]>(() => {
  const tenants = authStore.availableTenants.filter((t) => !t.is_global && t.tenant_id)
  if (tenants.length === 0 && authStore.availableTenants.some((t) => t.is_global)) {
    return [{
      tenant_id: null,
      tenant_name: t('routes.tenant-select.globalOnly'),
      roles: authStore.availableTenants.find((item) => item.is_global)?.roles ?? [],
      is_global: true,
    } as TenantInfo]
  }
  return tenants
})

/** Header 当前租户名称（仅全局视角时显示"全局"，避免显示"选择租户"造成困惑）。 */
const currentTenantLabel = computed(() => {
  if (authStore.currentTenant) return authStore.currentTenant.tenant_name || ''
  if (globalViewOnly.value) return t('routes.tenant-select.globalOnly')
  return t('routes.tenant-select.pending')
})

function handleTenantSwitch(tenantId: string | null) {
  if (!tenantId || tenantId === authStore.currentTenantId) return
  authStore.switchCurrentTenant(tenantId)
    .then(() => {
      ElMessage.success(t('routes.tenant-select.switched'))
      // 切换租户后刷新当前页面数据（router-view 依赖 fullPath，重新 push 触发重载）
      router.replace({ path: route.path, query: route.query })
    })
    .catch((error) => {
      ElMessage.error(error instanceof Error ? error.message : t('common.messages.error'))
    })
}

const helpDisplayWidth = computed(() => (settingsStore.helpCollapsed ? '36px' : '280px'))

const breadcrumbs = computed(() => {
  const matched = route.matched.filter(r => r.meta?.titleKey)
  return matched.map(r => ({
    path: r.path,
    title: t(r.meta.titleKey as string)
  }))
})

const isDashboardRoute = computed(() => route.name === 'dashboard')

function startResize(e: MouseEvent) {
  e.preventDefault()
  isResizing.value = true
  startX.value = e.clientX
  startWidth.value = settingsStore.sidebarWidth
  currentWidth.value = settingsStore.sidebarWidth
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
}

function onResize(e: MouseEvent) {
  if (!isResizing.value) return
  
  // 使用 RAF 优化性能
  if (rafId) cancelAnimationFrame(rafId)
  rafId = requestAnimationFrame(() => {
    const deltaX = e.clientX - startX.value
    const newWidth = Math.max(180, Math.min(400, startWidth.value + deltaX))
    currentWidth.value = newWidth
  })
}

function stopResize() {
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
  // 拖拽结束时保存到 store
  if (isResizing.value) {
    settingsStore.setSidebarWidth(currentWidth.value)
  }
  isResizing.value = false
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
}

function handleLocaleChange(lang: string) {
  appStore.setLocale(lang)
}

function goToProfile() {
  router.push('/authorization/personal')
}

function logout() {
  authStore.logout().finally(() => {
    router.push('/login')
  })
}

onUnmounted(() => {
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
})
</script>

<style scoped lang="scss">
.layout-container {
  height: 100vh;
  background-color: var(--el-bg-color-page);
  
    .left-aside {
      background-color: var(--el-menu-bg-color);
      border-right: 1px solid var(--el-border-color);
      display: flex;
      flex-direction: column;
      position: relative;
      overflow: visible; /* 关键：允许手柄显示在容器外部，不遮挡滚动条 */
      align-items: stretch;
      
      // 拖拽时禁用过渡动画
      &:not(.resizing) {
        transition: width 0.3s;
      }
      
      .logo {
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 20px;
        color: var(--el-color-primary);
        border-bottom: 1px solid var(--el-border-color);
        flex-shrink: 0;
        background: var(--el-bg-color);
      }
      
      .collapse-trigger {
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border-top: 1px solid var(--el-border-color);
        margin-top: auto;
        flex-shrink: 0;
        color: var(--el-text-color-secondary);
        
        &:hover {
          background-color: var(--el-menu-hover-bg-color);
          color: var(--el-color-primary);
        }
      }
      
      // 拖拽手柄样式
      .resize-handle {
        position: absolute;
        top: 0;
        left: 100%; /* 紧贴侧边栏右侧外部 */
        width: 20px; /* 加宽一倍至 20px */
        height: 100%;
        cursor: col-resize;
        z-index: 100;
        display: flex;
        justify-content: center; /* 线条居中 */
        align-items: center;
        transition: background-color 0.2s;
        
        // 可视化指示条 (拖拽线)
        &::after {
          content: '';
          width: 4px; /* 默认状态：细线 */
          height: 100%;
          border-radius: 10px; /* 圆角 */
          background-color: transparent;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        // 鼠标悬停时
        &:hover {
          &::after {
            width: 14px; /* 展开至接近全宽 */
            // 平和的灰蓝色 + 圆柱体渐变效果
            background-color: var(--el-fill-color-darker);
            background-image: linear-gradient(90deg, 
              transparent 0%, 
              rgba(0,0,0,0.05) 20%, 
              rgba(0,0,0,0.1) 50%, 
              rgba(0,0,0,0.05) 80%, 
              transparent 100%
            );
            box-shadow: none; /* 移除之前的发光 */
          }
        }
        
        // 拖拽中状态
        &.resizing {
          &::after {
            width: 14px;
            background-color: var(--el-color-info-light-5); /* 拖拽时稍微深一点 */
          }
        }
      }

      &.collapsed {
        align-items: center;

        .collapse-trigger {
          width: 100%;
          border-top: none;
        }
      }
    }
  
  .main-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--el-border-color);
    background-color: var(--el-bg-color); /* 使用新变量 */
    
    .header-left {
      display: flex;
      align-items: center;
    }
    
    .header-right {
      display: flex;
      align-items: center;
      gap: 16px;
      
      .el-dropdown-link {
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 4px;
        color: var(--el-text-color-regular);
        
        &:hover {
          color: var(--el-color-primary);
        }
      }
      
      .tenant-switcher {
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 4px;
        color: var(--el-text-color-regular);
        background: var(--el-fill-color-light);
        border-radius: 6px;
        padding: 4px 10px;
        font-size: 13px;
        
        &:hover {
          color: var(--el-color-primary);
          background: var(--el-fill-color);
        }
      }
      
      .tenant-static {
        margin-right: 0;
      }
      
      .user-info {
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--el-text-color-primary);
      }
    }
  }
  
  .content-container {
    overflow: hidden;
    background-color: var(--el-bg-color-page);
  }
  
  .main-content {
    overflow-y: auto;
    padding: 20px 24px 20px 24px;
    background-color: var(--el-bg-color-page);
  }

  .content-container.dashboard-route {
    height: 100%;
  }

  .main-content.dashboard-route {
    position: relative;
    height: 100%;
    padding: 0;
    overflow: hidden;
  }
  
  .right-help {
    border-left: 1px solid var(--el-border-color);
    background-color: var(--el-bg-color);
    padding: 12px 12px 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .right-help.collapsed {
    padding: 6px 4px 0;
    align-items: center;
  }

  .help-collapse-trigger {
    height: 40px;
    width: 40px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-top: auto;
    color: var(--el-text-color-secondary);
    transition: background-color 0.2s, color 0.2s;

    &:hover {
      background-color: var(--el-fill-color);
      color: var(--el-color-primary);
    }
  }
}

.layout-container.dashboard-fullscreen {
  .content-container {
    height: 100%;
  }

  .main-content {
    position: relative;
    padding: 0;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

